import React, { useEffect, useState } from 'react';
import { taskAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { CheckSquare, Plus, Clock } from 'lucide-react';
import { toast } from 'sonner';
import { formatDateTime } from '../lib/utils';

const Tasks = () => {
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [newTask, setNewTask] = useState({
    title: '',
    description: '',
    priority: 'medium',
    storeCode: ''
  });

  useEffect(() => {
    loadTasks();
  }, []);

  const loadTasks = async () => {
    try {
      const response = await taskAPI.getTasks({ page: 1, page_size: 50 });
      setTasks(response.data.tasks);
    } catch (error) {
      toast.error('Failed to load tasks');
    } finally {
      setLoading(false);
    }
  };

  const createTask = async () => {
    try {
      await taskAPI.createTask({
        ...newTask,
        source: { type: 'manual' }
      });
      toast.success('Task created successfully');
      setDialogOpen(false);
      setNewTask({ title: '', description: '', priority: 'medium', storeCode: '' });
      loadTasks();
    } catch (error) {
      toast.error('Failed to create task');
    }
  };

  const updateTaskStatus = async (taskId, newStatus) => {
    try {
      await taskAPI.updateTask(taskId, { status: newStatus });
      toast.success('Task updated');
      loadTasks();
    } catch (error) {
      toast.error('Failed to update task');
    }
  };

  const groupedTasks = {
    open: tasks.filter(t => t.status === 'open'),
    in_progress: tasks.filter(t => t.status === 'in_progress'),
    done: tasks.filter(t => t.status === 'done'),
    overdue: tasks.filter(t => t.status === 'overdue')
  };

  const columns = [
    { key: 'open', title: 'Open', color: 'bg-blue-100 border-blue-300', testId: 'column-open' },
    { key: 'in_progress', title: 'In Progress', color: 'bg-yellow-100 border-yellow-300', testId: 'column-in-progress' },
    { key: 'done', title: 'Done', color: 'bg-green-100 border-green-300', testId: 'column-done' },
    { key: 'overdue', title: 'Overdue', color: 'bg-red-100 border-red-300', testId: 'column-overdue' }
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 pb-20" data-testid="tasks-page">
      <div className="flex items-center justify-between fade-in">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 mb-2">Task Board</h1>
          <p className="text-slate-600">Manage and track store execution tasks</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button className="bg-blue-500 hover:bg-blue-600" data-testid="create-task-button">
              <Plus className="h-4 w-4 mr-2" />
              Create Task
            </Button>
          </DialogTrigger>
          <DialogContent data-testid="create-task-dialog">
            <DialogHeader>
              <DialogTitle>Create New Task</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label htmlFor="title">Title</Label>
                <Input
                  id="title"
                  data-testid="task-title-input"
                  value={newTask.title}
                  onChange={(e) => setNewTask({ ...newTask, title: e.target.value })}
                  placeholder="Task title"
                />
              </div>
              <div>
                <Label htmlFor="description">Description</Label>
                <Textarea
                  id="description"
                  data-testid="task-description-input"
                  value={newTask.description}
                  onChange={(e) => setNewTask({ ...newTask, description: e.target.value })}
                  placeholder="Task description"
                  rows={3}
                />
              </div>
              <div>
                <Label htmlFor="priority">Priority</Label>
                <Select value={newTask.priority} onValueChange={(value) => setNewTask({ ...newTask, priority: value })}>
                  <SelectTrigger data-testid="task-priority-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="low">Low</SelectItem>
                    <SelectItem value="medium">Medium</SelectItem>
                    <SelectItem value="high">High</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="storeCode">Store Code (Optional)</Label>
                <Input
                  id="storeCode"
                  data-testid="task-store-input"
                  value={newTask.storeCode}
                  onChange={(e) => setNewTask({ ...newTask, storeCode: e.target.value })}
                  placeholder="e.g., STORE001"
                />
              </div>
              <Button onClick={createTask} className="w-full bg-blue-500 hover:bg-blue-600" data-testid="submit-task-button">
                Create Task
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {/* Kanban Board */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 fade-in">
        {columns.map((column) => (
          <div key={column.key} data-testid={column.testId}>
            <Card className={`border-2 ${column.color}`}>
              <CardHeader>
                <CardTitle className="text-sm font-semibold flex items-center justify-between">
                  {column.title}
                  <span className="text-xs font-normal">{groupedTasks[column.key].length}</span>
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3 max-h-[60vh] overflow-y-auto scrollbar-thin">
                {groupedTasks[column.key].length === 0 ? (
                  <div className="text-center py-8 text-slate-400">
                    <CheckSquare className="h-8 w-8 mx-auto mb-2 opacity-50" />
                    <p className="text-sm">No tasks</p>
                  </div>
                ) : (
                  groupedTasks[column.key].map((task) => (
                    <Card key={task.taskId} className="p-4 bg-white border shadow-sm hover:shadow-md transition-shadow" data-testid={`task-${task.taskId}`}>
                      <div className="space-y-2">
                        <div className="flex items-start justify-between">
                          <h4 className="font-medium text-sm text-slate-900">{task.title}</h4>
                          <span className={`text-xs px-2 py-1 rounded-full ${
                            task.priority === 'high' ? 'bg-red-100 text-red-700' :
                            task.priority === 'medium' ? 'bg-yellow-100 text-yellow-700' :
                            'bg-green-100 text-green-700'
                          }`}>
                            {task.priority}
                          </span>
                        </div>
                        <p className="text-xs text-slate-600 line-clamp-2">{task.description}</p>
                        {task.storeCode && (
                          <p className="text-xs text-blue-600">Store: {task.storeCode}</p>
                        )}
                        <div className="flex items-center text-xs text-slate-500">
                          <Clock className="h-3 w-3 mr-1" />
                          {formatDateTime(task.createdAt)}
                        </div>
                        <div className="flex gap-2 pt-2">
                          {column.key === 'open' && (
                            <Button size="sm" variant="outline" className="text-xs h-7" onClick={() => updateTaskStatus(task.taskId, 'in_progress')}>
                              Start
                            </Button>
                          )}
                          {column.key === 'in_progress' && (
                            <Button size="sm" variant="outline" className="text-xs h-7" onClick={() => updateTaskStatus(task.taskId, 'done')}>
                              Complete
                            </Button>
                          )}
                        </div>
                      </div>
                    </Card>
                  ))
                )}
              </CardContent>
            </Card>
          </div>
        ))}
      </div>
    </div>
  );
};

export default Tasks;